import { useState } from "react";
import { uploadMedia } from "@/lib/adminCmsApi";
import { toast } from "sonner";
import { Upload, X, Image as ImageIcon, Plus, Trash2 } from "lucide-react";
import RichTextEditor from "@/components/admin/RichTextEditor";

/**
 * Generic per-section editor with per-type field schemas.
 */

function TextInput({ label, value, onChange, placeholder = "" }) {
  return (
    <label className="block">
      <span className="block font-mono text-[10px] uppercase tracking-[0.22em] text-ags-text-muted mb-1.5">{label}</span>
      <input type="text" value={value || ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
             className="w-full bg-white border border-[#0E2A57]/15 px-3 py-2 focus:border-[#0E2A57] focus:outline-none" />
    </label>
  );
}
function TextArea({ label, value, onChange, rows = 3 }) {
  return (
    <label className="block">
      <span className="block font-mono text-[10px] uppercase tracking-[0.22em] text-ags-text-muted mb-1.5">{label}</span>
      <textarea value={value || ""} rows={rows} onChange={(e) => onChange(e.target.value)}
                className="w-full bg-white border border-[#0E2A57]/15 px-3 py-2 focus:border-[#0E2A57] focus:outline-none" />
    </label>
  );
}

function ImageUpload({ label, value, onChange, folder = "ags/sections" }) {
  const [busy, setBusy] = useState(false);
  const handle = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const res = await uploadMedia(file, { folder, alt_text: label });
      onChange(res.url);
      toast.success("Uploaded");
    } catch (e) { toast.error("Upload failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="block">
      <span className="block font-mono text-[10px] uppercase tracking-[0.22em] text-ags-text-muted mb-1.5">{label}</span>
      <div className="flex items-center gap-3">
        {value ? (
          <div className="relative w-24 h-24 border border-[#0E2A57]/15 flex-shrink-0">
            <img src={value} alt={label} className="w-full h-full object-cover" />
            <button onClick={() => onChange("")} className="absolute -top-2 -right-2 bg-white border border-[#0E2A57]/20 p-1 rounded-full hover:bg-red-50"><X size={12} /></button>
          </div>
        ) : (
          <div className="w-24 h-24 border-2 border-dashed border-[#0E2A57]/30 flex items-center justify-center text-ags-text-muted"><ImageIcon size={20} /></div>
        )}
        <label className="cursor-pointer">
          <input type="file" accept="image/*,video/*" className="hidden" onChange={(e) => handle(e.target.files?.[0])} />
          <span className="inline-flex items-center gap-2 bg-white border border-[#0E2A57]/30 hover:bg-[#0E2A57] hover:text-white px-3 py-2 font-mono text-[10px] uppercase tracking-[0.22em] transition-colors">
            <Upload size={12} /> {busy ? "Uploading…" : value ? "Replace" : "Upload"}
          </span>
        </label>
      </div>
    </div>
  );
}

function ListEditor({ label, items, onChange, fields }) {
  // fields: [{name, label, type?}]
  const add = () => onChange([...(items || []), Object.fromEntries(fields.map((f) => [f.name, ""]))]);
  const update = (i, field, v) => {
    const next = [...(items || [])];
    next[i] = { ...next[i], [field]: v };
    onChange(next);
  };
  const remove = (i) => {
    const next = [...(items || [])]; next.splice(i, 1); onChange(next);
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-ags-text-muted">{label}</span>
        <button type="button" onClick={add} className="inline-flex items-center gap-1 bg-[#0E2A57] text-white px-2 py-1 font-mono text-[9px] uppercase tracking-[0.18em]">
          <Plus size={10} /> Add
        </button>
      </div>
      <div className="space-y-2">
        {(items || []).map((item, i) => (
          <div key={i} className="bg-white border border-[#0E2A57]/15 p-3 grid grid-cols-1 md:grid-cols-2 gap-2 relative">
            {fields.map((f) => (
              <label key={f.name} className="block">
                <span className="block text-[10px] text-ags-text-muted mb-1">{f.label}</span>
                {f.type === "textarea" ? (
                  <textarea value={item[f.name] || ""} rows={2} onChange={(e) => update(i, f.name, e.target.value)}
                            className="w-full bg-white border border-[#0E2A57]/10 px-2 py-1.5 text-sm focus:border-[#0E2A57] focus:outline-none" />
                ) : (
                  <input type="text" value={item[f.name] || ""} onChange={(e) => update(i, f.name, e.target.value)}
                         className="w-full bg-white border border-[#0E2A57]/10 px-2 py-1.5 text-sm focus:border-[#0E2A57] focus:outline-none" />
                )}
              </label>
            ))}
            <button type="button" onClick={() => remove(i)} className="absolute top-1 right-1 text-red-600 hover:bg-red-50 p-1"><Trash2 size={12} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function StringListEditor({ label, items, onChange }) {
  const add = () => onChange([...(items || []), ""]);
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-ags-text-muted">{label}</span>
        <button type="button" onClick={add} className="inline-flex items-center gap-1 bg-[#0E2A57] text-white px-2 py-1 font-mono text-[9px] uppercase tracking-[0.18em]">
          <Plus size={10} /> Add
        </button>
      </div>
      <div className="space-y-2">
        {(items || []).map((it, i) => (
          <div key={i} className="flex gap-2">
            <input type="text" value={it} onChange={(e) => { const n=[...items]; n[i]=e.target.value; onChange(n); }}
                   className="flex-1 bg-white border border-[#0E2A57]/15 px-3 py-2 text-sm focus:border-[#0E2A57] focus:outline-none" />
            <button type="button" onClick={() => { const n=[...items]; n.splice(i,1); onChange(n); }} className="px-2 text-red-600 hover:bg-red-50"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SectionEditor({ type, data, onChange }) {
  const set = (key, value) => onChange({ ...data, [key]: value });

  switch (type) {
    case "hero":
      return (
        <div className="space-y-4">
          <TextInput label="Eyebrow" value={data.eyebrow} onChange={(v) => set("eyebrow", v)} />
          <div className="grid md:grid-cols-3 gap-3">
            <TextInput label="Title (before italic)" value={data.title_pre} onChange={(v) => set("title_pre", v)} />
            <TextInput label="Title (italic accent)" value={data.title_italic} onChange={(v) => set("title_italic", v)} />
            <TextInput label="Title (after italic)" value={data.title_post} onChange={(v) => set("title_post", v)} />
          </div>
          <TextArea label="Body / Subtext" value={data.body} onChange={(v) => set("body", v)} rows={4} />
          <div className="grid md:grid-cols-2 gap-3">
            <TextInput label="Primary CTA — Label" value={data.primary_cta_label} onChange={(v) => set("primary_cta_label", v)} />
            <TextInput label="Primary CTA — URL" value={data.primary_cta_href} onChange={(v) => set("primary_cta_href", v)} />
            <TextInput label="Secondary CTA — Label" value={data.secondary_cta_label} onChange={(v) => set("secondary_cta_label", v)} />
            <TextInput label="Secondary CTA — URL" value={data.secondary_cta_href} onChange={(v) => set("secondary_cta_href", v)} />
          </div>
          <ImageUpload label="Background Image" value={data.image_url} onChange={(v) => set("image_url", v)} />
          <ListEditor label="KPI Items" items={data.kpis} onChange={(v) => set("kpis", v)}
                      fields={[{name:"k",label:"Value"},{name:"v",label:"Label"}]} />
        </div>
      );
    case "about":
      return (
        <div className="space-y-4">
          <TextInput label="Eyebrow" value={data.eyebrow} onChange={(v) => set("eyebrow", v)} />
          <div className="grid md:grid-cols-2 gap-3">
            <TextInput label="Title (before italic)" value={data.title_pre} onChange={(v) => set("title_pre", v)} />
            <TextInput label="Title (italic accent)" value={data.title_italic} onChange={(v) => set("title_italic", v)} />
          </div>
          <StringListEditor label="Paragraphs" items={data.paragraphs} onChange={(v) => set("paragraphs", v)} />
        </div>
      );
    case "ags_meaning":
      return (
        <div className="space-y-4">
          <TextInput label="Eyebrow" value={data.eyebrow} onChange={(v) => set("eyebrow", v)} />
          <TextInput label="Title" value={data.title} onChange={(v) => set("title", v)} />
          <ListEditor label="Letters (A, G, S)" items={data.letters} onChange={(v) => set("letters", v)}
                      fields={[{name:"letter",label:"Letter"},{name:"title",label:"Title"},{name:"body",label:"Body",type:"textarea"}]} />
        </div>
      );
    case "founder_quote":
      return (
        <div className="space-y-4">
          <TextInput label="Eyebrow" value={data.eyebrow} onChange={(v) => set("eyebrow", v)} />
          <TextArea label="Quote" value={data.quote} onChange={(v) => set("quote", v)} rows={4} />
          <TextInput label="Attribution" value={data.attribution} onChange={(v) => set("attribution", v)} />
        </div>
      );
    case "services_grid":
      return (
        <div className="space-y-4">
          <TextInput label="Eyebrow" value={data.eyebrow} onChange={(v) => set("eyebrow", v)} />
          <div className="grid md:grid-cols-2 gap-3">
            <TextInput label="Title (before italic)" value={data.title_pre} onChange={(v) => set("title_pre", v)} />
            <TextInput label="Title (italic accent)" value={data.title_italic} onChange={(v) => set("title_italic", v)} />
          </div>
          <TextArea label="Intro" value={data.intro} onChange={(v) => set("intro", v)} />
        </div>
      );
    case "trust_strip":
      return (
        <div className="space-y-4">
          <TextInput label="Title" value={data.title} onChange={(v) => set("title", v)} />
          <ListEditor label="Badges" items={data.items} onChange={(v) => set("items", v)}
                      fields={[{name:"icon",label:"Icon name (lucide)"},{name:"label",label:"Label"},{name:"sub",label:"Subtext"}]} />
        </div>
      );
    case "process_steps":
      return (
        <div className="space-y-4">
          <TextInput label="Eyebrow" value={data.eyebrow} onChange={(v) => set("eyebrow", v)} />
          <div className="grid md:grid-cols-3 gap-3">
            <TextInput label="Title pre" value={data.title_pre} onChange={(v) => set("title_pre", v)} />
            <TextInput label="Italic accent" value={data.title_italic} onChange={(v) => set("title_italic", v)} />
            <TextInput label="Title post" value={data.title_post} onChange={(v) => set("title_post", v)} />
          </div>
          <TextArea label="Intro" value={data.intro} onChange={(v) => set("intro", v)} />
          <ListEditor label="Steps" items={data.steps} onChange={(v) => set("steps", v)}
                      fields={[{name:"title",label:"Step title"},{name:"body",label:"Step body",type:"textarea"}]} />
        </div>
      );
    case "cta":
      return (
        <div className="space-y-4">
          <TextInput label="Eyebrow" value={data.eyebrow} onChange={(v) => set("eyebrow", v)} />
          <TextInput label="Title" value={data.title} onChange={(v) => set("title", v)} />
          <TextArea label="Body" value={data.body} onChange={(v) => set("body", v)} />
          <div className="grid md:grid-cols-2 gap-3">
            <TextInput label="Button Label" value={data.button_label} onChange={(v) => set("button_label", v)} />
            <TextInput label="Button URL" value={data.button_href} onChange={(v) => set("button_href", v)} />
          </div>
          <ImageUpload label="Background Image (optional)" value={data.image_url} onChange={(v) => set("image_url", v)} />
        </div>
      );
    case "image":
      return (
        <div className="space-y-4">
          <ImageUpload label="Image" value={data.url} onChange={(v) => set("url", v)} />
          <TextInput label="Caption (optional)" value={data.caption} onChange={(v) => set("caption", v)} />
        </div>
      );
    case "video":
      return (
        <div className="space-y-4">
          <ImageUpload label="Video File (Cloudinary)" value={data.url} onChange={(v) => set("url", v)} />
          <TextInput label="OR — External URL (YouTube/Vimeo)" value={data.external_url} onChange={(v) => set("external_url", v)} />
          <div className="grid md:grid-cols-3 gap-3">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!data.autoplay} onChange={(e) => set("autoplay", e.target.checked)} /> Autoplay</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!data.loop} onChange={(e) => set("loop", e.target.checked)} /> Loop</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!data.muted} onChange={(e) => set("muted", e.target.checked)} /> Muted</label>
          </div>
        </div>
      );
    case "rich_text":
      return <TextArea label="Content (HTML allowed)" value={data.html} onChange={(v) => set("html", v)} rows={8} />;
    case "custom_html":
      return (
        <div className="space-y-2">
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2">⚠️ Custom HTML — be careful, this can break your layout.</p>
          <TextArea label="Raw HTML" value={data.html} onChange={(v) => set("html", v)} rows={10} />
        </div>
      );
    case "faq":
      return (
        <div className="space-y-4">
          <TextInput label="Eyebrow (optional)" value={data.eyebrow} onChange={(v) => set("eyebrow", v)} />
          <TextInput label="Title (optional)" value={data.title} onChange={(v) => set("title", v)} />
          <ListEditor label="Q&A items" items={data.items} onChange={(v) => set("items", v)}
                      fields={[{name:"q",label:"Question"},{name:"a",label:"Answer",type:"textarea"}]} />
        </div>
      );
    case "testimonials":
      return (
        <div className="space-y-4">
          <TextInput label="Title" value={data.title} onChange={(v) => set("title", v)} />
          <ListEditor label="Testimonials" items={data.items} onChange={(v) => set("items", v)}
                      fields={[{name:"quote",label:"Quote",type:"textarea"},{name:"author",label:"Author"},{name:"role",label:"Role / Company"}]} />
        </div>
      );
    case "kpi_strip":
      return (
        <div className="space-y-4">
          <TextInput label="Title (optional)" value={data.title} onChange={(v) => set("title", v)} />
          <ListEditor label="KPI items" items={data.items} onChange={(v) => set("items", v)}
                      fields={[{name:"k",label:"Value"},{name:"v",label:"Label"}]} />
        </div>
      );

    case "team_gallery":
      return (
        <div className="space-y-4">
          <div className="bg-[#BEE7F5]/30 border border-[#2BB6E5] p-4 text-xs text-[#0E2A57]">
            <strong className="font-mono uppercase tracking-[0.18em] text-[10px]">CMS-driven</strong> — team members are managed in the{" "}
            <a href="/admin/collections/team" className="underline font-semibold">Team collection</a>.
            Only the section heading is editable here.
          </div>
          <TextInput label="Eyebrow" value={data.eyebrow} onChange={(v) => set("eyebrow", v)} />
          <TextInput label="Title" value={data.title} onChange={(v) => set("title", v)} />
          <TextArea label="Subtitle" value={data.subtitle} onChange={(v) => set("subtitle", v)} rows={2} />
        </div>
      );

    case "compliance":
      return (
        <div className="space-y-4">
          <div className="bg-[#BEE7F5]/30 border border-[#2BB6E5] p-4 text-xs text-[#0E2A57]">
            <strong className="font-mono uppercase tracking-[0.18em] text-[10px]">CMS-driven</strong> — accreditation badges come from the{" "}
            <a href="/admin/collections/partners" className="underline font-semibold">Partners collection</a>{" "}
            (category = <code>accreditation</code>). Only the section heading is editable here.
          </div>
          <TextInput label="Eyebrow" value={data.eyebrow} onChange={(v) => set("eyebrow", v)} />
          <TextInput label="Title" value={data.title} onChange={(v) => set("title", v)} />
          <TextArea label="Subtitle" value={data.subtitle} onChange={(v) => set("subtitle", v)} rows={2} />
        </div>
      );

    case "green_pledge":
      return (
        <div className="space-y-4">
          <TextInput label="Eyebrow" value={data.eyebrow} onChange={(v) => set("eyebrow", v)} placeholder="Our Green Pledge" />
          <TextInput label="Title" value={data.title} onChange={(v) => set("title", v)} placeholder="Cleaner sites. Cleaner future." />
          <TextArea label="Body" value={data.body} onChange={(v) => set("body", v)} rows={4} />
          <ListEditor
            label="Bullet points"
            items={(data.bullets || []).map((b) => ({ v: b }))}
            onChange={(rows) => set("bullets", rows.map((r) => r.v).filter(Boolean))}
            fields={[{ name: "v", label: "Bullet text" }]}
          />
          <ImageUpload label="Image" value={data.image_url} onChange={(v) => set("image_url", v)} />
        </div>
      );

    // Sections still using built-in React templates (no fields)
    case "site_leadership":
    case "expertise":
    case "facility_management":
    case "quote_form":
      return (
        <div className="text-sm text-ags-text-muted bg-white border border-[#0E2A57]/10 p-4">
          This section is a built-in template rendered by AGS components. Re-order, disable, or remove the section.
        </div>
      );

    default:
      return <pre className="bg-white border border-[#0E2A57]/10 p-3 text-xs overflow-auto">{JSON.stringify(data, null, 2)}</pre>;
  }
}
